// client code is ES6 Module
import 'text-encoding-utf-8/lib/encoding.js';
import { Observable, type FetchFunction, type GraphQLResponseWithData } from 'relay-runtime';
import fetchMultipart from 'fetch-multipart-graphql';
import { extractFiles } from 'extract-files';

import { createNetworkError } from './src/createNetworkError.ts';
import { GRAPHQL_PATH } from './src/constants.ts';
import { getClientHeaders } from './src/getClientHeaders.ts';
import { type GraphQLRequestBody } from './index.ts';
import { createOrWaitForChallenge } from './src/createOrWaitForChallenge.tsx';
import serverVars from 'server-vars';

const clientChallengeRetries: boolean = serverVars.get('enablers.clientChallengeRetries') ?? false;

export type CreateFetcherArgs = {
    getQueries: () => Record<string, string | undefined>;
    userType: 'buyer' | 'seller' | 'internal';
    getGraphQLNetworkContext?: () => string | null;
    additionalHeaders?: Record<string, string>;
};

export function createFetcher({
    getQueries = () => ({}),
    userType,
    getGraphQLNetworkContext = () => null,
    additionalHeaders = {},
}: CreateFetcherArgs): FetchFunction {
    return function fetchQuery(operation, variables, cacheConfig) {
        return Observable.create(sink => {
            let retries = 0;
            const maxRetries = 2;

            function fetch(): void {
                let body;
                const headers = {
                    ...getClientHeaders(
                        getQueries,
                        userType,
                        getGraphQLNetworkContext,
                        operation,
                        cacheConfig
                    ),
                    ...additionalHeaders,
                };
                const operations: GraphQLRequestBody = { variables };
                if (operation.id) {
                    // persisted query support
                    operations.id = operation.id;
                } else {
                    operations.query = operation.text;
                }

                const {
                    clone: extractedOperations,
                    files,
                }: {
                    clone: GraphQLRequestBody;
                    files: Map<File, string>;
                } = extractFiles(operations);

                if (files.size) {
                    if (!window.FormData) {
                        throw new Error('Uploading files without `FormData` not supported.');
                    }

                    const formData = new FormData();
                    formData.append('operations', JSON.stringify(extractedOperations));

                    const pathMap: Record<string, string> = {};
                    let i = 0;
                    files.forEach(paths => {
                        pathMap[(++i).toString()] = paths;
                    });
                    formData.append('map', JSON.stringify(pathMap));

                    // using a separate forEach here to properly order multipart fields
                    // files should follow 'map', set in the loop above
                    i = 0;
                    files.forEach((paths, file) => {
                        formData.append((++i).toString(), file, file.name);
                    });

                    body = formData;
                } else {
                    headers['Content-Type'] = 'application/json';
                    body = JSON.stringify(operations);
                }

                let hasSentPayloads = false;
                let isChallenge = false;

                fetchMultipart(GRAPHQL_PATH, {
                    method: 'post',
                    headers,
                    credentials: 'same-origin', // to ensure cookies send correctly see https://github.com/github/fetch#caveats
                    body,
                    onNext: async (
                        payloads: ReadonlyArray<GraphQLResponseWithData & { code?: string }>,
                        meta?: { responseHeaders?: Headers; status?: number }
                    ) => {
                        let hasErrors = false;
                        for (const payload of payloads) {
                            if (
                                clientChallengeRetries &&
                                typeof payload.data === 'undefined' &&
                                payload?.code === 'FS_CH_TI' &&
                                meta?.status === 400 &&
                                retries <= maxRetries &&
                                !hasSentPayloads
                            ) {
                                retries++;
                                try {
                                    isChallenge = true;
                                    await createOrWaitForChallenge();
                                    isChallenge = false;
                                    fetch();
                                } catch (e) {
                                    hasErrors = true;
                                    const error = createNetworkError(
                                        operation,
                                        payload,
                                        meta?.responseHeaders
                                    );
                                    sink.error(error);
                                }
                                return;
                            } else if (payload.errors) {
                                hasErrors = true;
                                // all errors are critical, throw so relay treats as an error
                                const error = createNetworkError(
                                    operation,
                                    payload,
                                    meta?.responseHeaders
                                );
                                sink.error(error);
                            }
                        }
                        if (!hasErrors) {
                            sink.next(payloads);
                            hasSentPayloads = true;
                        }
                    },
                    onError: (err: Error) => {
                        const fetchError = new Error(
                            `"${operation.name}": Relay network layer error.`,
                            { cause: err }
                        );
                        return sink.error(fetchError);
                    },
                    onComplete: () => {
                        if (!isChallenge) {
                            sink.complete();
                        }
                    },
                });
            }
            fetch();
        });
    };
}
